import React from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, Grid } from '@mui/material';
import {
  FilterOptions,
  Plan,
  TimePeriodData,
  SCOPE_KEYS,
  AppSettingsResponse,
} from '@shared/schema';
import { useTranslation } from '@/core/i18n/i18n';
import { apiRequest } from '@/lib/queryClient';
import AddCircleIcon from '@/static/icons/add-circle.svg?url';
import AlertaVMIcon from '@/static/icons/alerta-VM.svg?url';
import ExcelIcon from '@/static/icons/XLSdocument.svg?url';
import CSVIcon from '@/static/icons/CVSdocument.svg?url';
import { useNavigate } from 'react-router-dom';

type AnnualPlan = Plan;
import StyledTable from './StyledTable';
import OutOfTimeRequestDialog from './Shared/OutOfTimeRequestDialog';
import { getAnnualPlanningPeriodState, getCreatePlanAvailability } from './availabilityRules';
import { OutOfTimeRequestStatusState } from '@/core/types/state';

interface AnnualPlanTableProps {
  plans: AnnualPlan[];
  periods: TimePeriodData[];
  filterOptions: FilterOptions;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  exportAnnualPlansToCSV: () => void;
  exportAnnualPlansToExcel: () => void;
  setCurrentVersion: (versionId: number) => void;
  setFulfillmentVersion: (versionId: number) => void;
  setReadOnlyMode: (readOnly: boolean) => void;
  setCreatingOutOfTimePlan: (creatingOutOfTime: boolean, requestId?: number | null) => void;
  setApYear: (year: number | null) => void;
  setCurrentBusinessUnit: (businessUnit: string | null) => void;
  isAnyLoading: boolean;
  deleteAnnualPlan: (id: number) => void;
  setCurrentPlanId: (planId: number | null) => void;
  setSelectedHierarchicalModel: (hierarchicalModelId: number | null) => void;
  reopenPlanVersion?: (versionId: number, reason: string) => void;
  reopenFulfillmentVersion?: (fulfillmentVersionId: number, reason: string) => void;
  reloadPage: () => void;
  anularPlanVersion: (apId: number) => void;
  returnPlanToDefinition: (apId: number) => Promise<void> | void;
  userRoles: string[];
  loadingUserRoles: boolean;
  outOfTimeRequestStatus: OutOfTimeRequestStatusState | null;
  loadingOutOfTimeRequestStatus: boolean;
  submittingOutOfTimeRequest: boolean;
  fetchOutOfTimeRequestStatus: () => void;
  submitOutOfTimeRequest: (reason: string) => Promise<void>;
}

const AnnualPlanTable: React.FC<AnnualPlanTableProps> = ({
  plans,
  periods,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  filterOptions,
  exportAnnualPlansToCSV,
  exportAnnualPlansToExcel,
  setCurrentVersion,
  setFulfillmentVersion,
  setReadOnlyMode,
  setCreatingOutOfTimePlan,
  setApYear,
  setCurrentBusinessUnit,
  isAnyLoading,
  deleteAnnualPlan,
  setCurrentPlanId,
  setSelectedHierarchicalModel,
  reopenPlanVersion,
  reopenFulfillmentVersion,
  reloadPage,
  anularPlanVersion,
  returnPlanToDefinition,
  userRoles,
  loadingUserRoles,
  outOfTimeRequestStatus,
  loadingOutOfTimeRequestStatus,
  submittingOutOfTimeRequest,
  fetchOutOfTimeRequestStatus,
  submitOutOfTimeRequest,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => onPageChange(value);

  const handleChangeRowsPerPage = (event: { target?: { value?: unknown } }) => {
    const rawValue = event.target?.value;
    const value = typeof rawValue === 'number' ? rawValue : parseInt(String(rawValue ?? '10'), 10);
    onPageSizeChange(Number.isNaN(value) ? 10 : value);
  };

  const periodState = React.useMemo(() => getAnnualPlanningPeriodState(periods), [periods]);

  const tableRows = [
    t('annualPlanning.id'),
    t('annualPlanning.businessUnit'),
    t('annualPlanning.year'),
    t('annualPlanning.stage'),
    t('annualPlanning.status'),
    t('annualPlanning.responsibleUser'),
    t('annualPlanning.creationDate'),
    t('annualPlanning.actions'),
  ];

  const [showOutOfTimeDialog, setShowOutOfTimeDialog] = React.useState(false);
  const [disableViewButtonInEnvironmentScope, setDisableViewButtonInEnvironmentScope] =
    React.useState(false);
  const [scopeConstant, setScopeConstant] = React.useState<string | null>(null);
  const outOfTimeStatusRequestedRef = React.useRef(false);
  const definitionRealPeriodActive = periodState.definitionPeriodActive;

  const {
    isAdmin,
    isSelfAssessment,
    canCreateDelayedPlanAsAdmin,
    canCreateDelayedPlanWithApproval,
    shouldShowRequestButton,
  } = React.useMemo(
    () =>
      getCreatePlanAvailability({
        userRoles,
        definitionPeriodActive: periodState.definitionPeriodActive,
        approvedRequest: outOfTimeRequestStatus?.approvedRequest,
      }),
    [outOfTimeRequestStatus?.approvedRequest, periodState.definitionPeriodActive, userRoles],
  );
  /* const shouldDisableViewButton =
    disableViewButtonInEnvironmentScope && scopeConstant === SCOPE_KEYS.ENVIRONMENT && !isAdmin; */

  React.useEffect(() => {
    let cancelled = false;

    const loadAppSettings = async () => {
      try {
        const response = await apiRequest('GET', '/api/app-settings');
        const payload = (await response.json()) as AppSettingsResponse;
        if (cancelled) return;
        setDisableViewButtonInEnvironmentScope(
          payload?.settings?.annualPlanning?.disableViewButtonInEnvironmentScope === true,
        );
        setScopeConstant(payload?.scopeConstant ?? null);
      } catch (error) {
        console.error('[AnnualPlanTable] Error loading app settings', error);
      }
    };

    loadAppSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (outOfTimeStatusRequestedRef.current) return;
    if (
      loadingUserRoles ||
      userRoles.length === 0 ||
      isAdmin ||
      !isSelfAssessment ||
      loadingOutOfTimeRequestStatus ||
      outOfTimeRequestStatus !== null
    ) {
      return;
    }
    outOfTimeStatusRequestedRef.current = true;
    fetchOutOfTimeRequestStatus();
  }, [
    fetchOutOfTimeRequestStatus,
    isAdmin,
    isSelfAssessment,
    loadingOutOfTimeRequestStatus,
    loadingUserRoles,
    outOfTimeRequestStatus,
    userRoles.length,
  ]);

  const handleSubmitOutOfTimeRequest = React.useCallback(
    async (reason: string) => {
      try {
        await submitOutOfTimeRequest(reason);
        setShowOutOfTimeDialog(false);
      } catch {
        /* feedback handled in thunk */
      }
    },
    [submitOutOfTimeRequest],
  );

  return (
    <Box>
      {/* Row 1: Add New Annual Plan Button */}
      <Grid container justifyContent="flex-end" sx={{ mb: 2 }} spacing={2}>
        {definitionRealPeriodActive && (isAdmin || isSelfAssessment) && (
          <Grid size="auto">
            <Button
              variant="contained"
              disabled={isAnyLoading}
              startIcon={
                <img src={AddCircleIcon} alt="Add New Plan" style={{ width: 16, height: 16 }} />
              }
              onClick={() => {
                setCreatingOutOfTimePlan(false);
                navigate('/new-annual-plan');
              }}
              sx={{
                bgcolor: '#04B763',
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                height: 36,
                border: '1px solid #04B763',
                '&:hover': { bgcolor: '#20ba59', boxShadow: 'none' },
              }}
            >
              {t('annualPlanning.addNewPlan')}
            </Button>
          </Grid>
        )}
        {(canCreateDelayedPlanAsAdmin || canCreateDelayedPlanWithApproval) && (
          <Grid size="auto">
            <Button
              variant="contained"
              disabled={isAnyLoading}
              startIcon={
                <img src={AlertaVMIcon} alt="Delayed Plan" style={{ width: 16, height: 16 }} />
              }
              onClick={() => {
                setCreatingOutOfTimePlan(true);
                navigate('/new-annual-plan');
              }}
              sx={{
                bgcolor: '#C99D00',
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                border: '1px solid #C99D00',
                height: 36,
                '&:hover': { bgcolor: '#C99D00', boxShadow: 'none' },
              }}
            >
              {t('annualPlanning.addDelayedPlan')}
            </Button>
          </Grid>
        )}
        {shouldShowRequestButton && (
          <Grid size="auto">
            <Button
              variant="contained"
              disabled={isAnyLoading || loadingOutOfTimeRequestStatus}
              startIcon={
                <img
                  src={AlertaVMIcon}
                  alt="Request Out Of Time"
                  style={{ width: 16, height: 16 }}
                />
              }
              onClick={() => setShowOutOfTimeDialog(true)}
              sx={{
                bgcolor: '#005670',
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                border: '1px solid #005670',
                height: 36,
                '&:hover': { bgcolor: '#006f8f', boxShadow: 'none' },
              }}
            >
              {t('annualPlanning.requestOutOfTimePlan')}
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Fila 2: Barra de acciones con selector y botones de exportación */}
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid size="auto">
          <Grid container alignItems="center" spacing={1.5}>
            <Grid size="auto">
              <Typography variant="body2">{t('table.show')}</Typography>
            </Grid>
            <Grid size="auto">
              <FormControl size="small">
                <Select
                  value={pageSize}
                  onChange={handleChangeRowsPerPage}
                  sx={{ minWidth: 80, height: 32, backgroundColor: 'background.paper' }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size="auto">
              <Typography variant="body2">{t('table.records')}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid size="auto">
          <Grid container spacing={1}>
            {[
              {
                label: 'Excel',
                icon: (
                  <img
                    src={ExcelIcon}
                    alt={t('annualPlanning.exportExcel')}
                    style={{ width: 16, height: 16 }}
                  />
                ),
                action: () => exportAnnualPlansToExcel(),
              },
              {
                label: 'CSV',
                icon: (
                  <img
                    src={CSVIcon}
                    alt={t('annualPlanning.exportCSV')}
                    style={{ width: 16, height: 16 }}
                  />
                ),
                action: () => exportAnnualPlansToCSV(),
              },
            ].map((b) => (
              <Button
                key={b.label}
                variant="contained"
                startIcon={b.icon}
                disabled={isAnyLoading}
                onClick={b.action}
                sx={{
                  bgcolor: '#E3E8EA',
                  color: '#4c4c4c',
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  px: 1.5,
                  height: 36,
                  border: '1px solid #A5B2BB',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#dcdcdc', boxShadow: 'none' },
                }}
              >
                {b.label}
              </Button>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Tabla */}
      <StyledTable
        totalPages={totalPages}
        page={currentPage}
        handleChangePage={handleChangePage}
        paginatedPlans={plans}
        tableRows={tableRows}
        filterOptions={filterOptions}
        setCurrentVersion={setCurrentVersion}
        setFulfillmentVersion={setFulfillmentVersion}
        setReadOnlyMode={setReadOnlyMode}
        periodState={periodState}
        setApYear={setApYear}
        setCurrentBusinessUnit={setCurrentBusinessUnit}
        isAnyLoading={isAnyLoading}
        deleteAnnualPlan={deleteAnnualPlan}
        setCurrentPlanId={setCurrentPlanId}
        setSelectedHierarchicalModel={setSelectedHierarchicalModel}
        reopenPlanVersion={reopenPlanVersion}
        reopenFulfillmentVersion={reopenFulfillmentVersion}
        reloadPage={reloadPage}
        anularPlanVersion={anularPlanVersion}
        returnPlanToDefinition={returnPlanToDefinition}
        roles={userRoles}
        /* disableViewButton={shouldDisableViewButton} */
      />
      <OutOfTimeRequestDialog
        open={showOutOfTimeDialog}
        onClose={() => setShowOutOfTimeDialog(false)}
        isSubmitting={submittingOutOfTimeRequest}
        onSubmit={handleSubmitOutOfTimeRequest}
      />
    </Box>
  );
};

export default AnnualPlanTable;
